<template>
  <div data-testid="configuration-form" class="configuration-form">
    <VerticalWizardContent :title="title">
      <label class="string-input">
        <div class="label"><Localization message="create-plan-from" /></div>
        <MonthScroller
          v-model="from"
          :message="validationMessageFrom"
          :state="getFieldState($v.from)"
          :disabled="allowStartEdit"
          @input="input"
          @select="onSelected($event)"
          :isSearchable="isSearch"
          @open="onOpenDropdown"
          :timezone="getTimeZone"
        />
      </label>
      <label class="string-input">
        <div class="label"><Localization message="create-plan-to" /></div>
        <MonthScroller
          v-model="to"
          monthPart="end"
          :message="validationMessageTo"
          :state="getFieldState($v.to)"
          :disabled="allowStartEdit"
          @input="input"
          @select="onSelected($event)"
          :isSearchable="isSearch"
          @open="onOpenDropdown"
          :timezone="getTimeZone"
        />
      </label>
      <label
        class="string-input"
        :class="{ 'input-error': $v.rosterConfigurations.$error }"
      >
        <div class="label">
          <Localization message="create-plan-roster-configuration" />
        </div>
        <AlSelect
          v-model="rosterConfigurations"
          :options="rosterOptions"
          :multiple="true"
          @input="input"
          :is-loading="isLoading"
        >
        </AlSelect>
        <div class="message">
          <div v-if="!$v.rosterConfigurations.$error">
            <Localization
              message="create-plan-roster-configuration-explanation"
            />
          </div>
          <div v-if="$v.rosterConfigurations.$error">
            {{ validationMessageRosterConfigurations }}
          </div>
        </div>
      </label>
      <label
        class="string-input"
        :class="{ 'input-error': $v.timezone.$error }"
      >
        <div class="label"><Localization message="create-plan-timezone" /></div>
        <AlSelect
          v-model="timezone"
          :options="timezoneList"
          :searchable="isSearch"
          @input="input"
          @select="onSelected($event)"
          @open="onOpenDropdown"
        >
        </AlSelect>
        <div class="message">
          <div v-if="!$v.timezone.$error">
            <Localization message="create-plan-timezone-explanation" />
          </div>
          <div v-if="$v.timezone.$error">{{ validationMessageTimezone }}</div>
        </div>
      </label>
      <label
        class="string-input"
        :class="{ 'input-error': $v.forecast.$error }"
      >
        <div class="label"><Localization message="create-plan-forecast" /></div>
        <AlSelect
          v-model="forecast"
          :options="forecastListOptions"
          @input="input"
          :is-loading="isLoading"
        >
        </AlSelect>
        <div class="message">
          <div v-if="!$v.forecast.$error">
            <Localization message="create-plan-forecast-explanation" />
          </div>
          <div v-if="$v.forecast.$error">{{ validationMessageForecast }}</div>
        </div>
      </label>
      <label
        class="string-input"
        v-featureflow:isOn="'capacity-planner-scenarios'"
      >
        <div class="label">
          <Localization message="create-plan-scenarios" />
        </div>
        <AlSelect
          v-model="scenarios"
          :options="scenarioListOptions"
          @input="input"
          :multiple="true"
          :is-loading="isLoading"
        >
        </AlSelect>
        <div class="message">
          <Localization message="create-plan-scenarios-explanation" />
        </div>
      </label>
    </VerticalWizardContent>
    <AlButton
      v-if="showSubmitButton"
      type="btn"
      :text="submitButtonText"
      @click="save"
    />
  </div>
</template>

<script>
import { AlSelect, AlButton } from "@playvox/almendra-components";
import {
  VerticalWizardContent,
  MonthScroller,
  Forecast,
  Scenario,
  Translator,
  Localization,
} from "@agyletime/wfm-vue-common";
import { getTimezonesList } from "@agyletime/date-utils";
import { getScenariosThatCanBeUsed } from "../../utils/scenarios";

export default {
  name: "ConfigurationForm",
  components: {
    VerticalWizardContent,
    AlSelect,
    MonthScroller,
    AlButton,
    Localization,
  },
  mixins: [Translator],
  props: {
    value: {
      type: Object,
      required: true,
    },
    validationsSchema: {
      type: Object,
      required: true,
    },
    submitButtonText: {
      type: String,
      default: "Submit",
    },
    showSubmitButton: {
      type: Boolean,
      default: true,
    },
    isNewRecord: {
      type: Boolean,
      default: true,
    },
    rosterConfigurationList: {
      type: Array,
      required: true,
    },
  },
  watch: {
    value: {
      handler(newValue) {
        const newValues = this.handleIncomingProps(newValue, this.timezoneList);

        Object.keys(newValues).forEach((key) => {
          this[key] = newValues[key];
        });
      },
      deep: true,
    },
  },
  async created() {
    // Get async dropdown options
    const promises = await Promise.all([
      this.getForecasts(),
      this.getScenarios(),
    ]);
    this.forecastListOptions = promises[0];
    this.scenarioListOptionsUnfiltered = promises[1];

    // Set values based on async data
    if (this.value.rosterConfigurations) {
      this.rosterConfigurations = this.value.rosterConfigurations
        .map((v) => {
          return this.rosterOptions.find((r) => r.id === (v._id || v.id)) || v;
        })
        .filter(Boolean);
    }
    if (this.value.forecast) {
      this.forecast = this.forecastListOptions.find(
        (f) => f.id === this.value.forecast.id
      );
    }

    this.scenarioListOptions = getScenariosThatCanBeUsed({
      allScenarios: this.scenarioListOptionsUnfiltered || [],
      rosters: this.rosterOptions,
      startDate: this.from,
      endDate: this.to,
    });

    if (this.value.scenarios) {
      this.scenarios = this.value.scenarios
        .map((v) =>
          this.scenarioListOptions.find((s) => s.id === (v._id || v.id))
        )
        .filter(Boolean);
    }

    this.isLoading = false;
  },
  data() {
    const conf = this.value;
    const timezones = this.getTimezones();
    return {
      isSearch: true,
      timezoneList: timezones,
      forecastListOptions: [],
      scenarioListOptions: [],
      ...this.handleIncomingProps(conf, timezones),
      isLoading: true,
    };
  },
  computed: {
    rosterOptions() {
      return this.rosterConfigurationList || [];
    },
    getTimeZone() {
      return this.timezone?.name || undefined;
    },
    allowStartEdit() {
      return !this.isNewRecord;
    },
    validationMessageFrom() {
      if (!this.$v.from.required) {
        return this.translate("validation-mandatory", {
          field: this.translate("create-plan-from"),
        });
      }
      return "";
    },
    validationMessageTo() {
      if (!this.$v.from.required) {
        return this.translate("validation-mandatory", {
          field: this.translate("create-plan-to"),
        });
      }
      if (!this.$v.to.isAfter) {
        return this.translate("validation-after-or-equal-date-field", {
          field: this.translate("create-plan-to"),
          other: this.translate("create-plan-from"),
        });
      }
      return "";
    },
    validationMessageRosterConfigurations() {
      if (!this.$v.rosterConfigurations.required) {
        return this.translate("validation-mandatory", {
          field: this.translate("create-plan-roster-configuration"),
        });
      }
      return "";
    },
    validationMessageTimezone() {
      if (!this.$v.timezone.required) {
        return this.translate("validation-mandatory", {
          field: this.translate("create-plan-timezone"),
        });
      }
      return "";
    },
    validationMessageForecast() {
      if (!this.$v.forecast.required) {
        return this.translate("validation-mandatory", {
          field: this.translate("create-plan-forecast"),
        });
      }
      return "";
    },
  },
  methods: {
    onOpenDropdown() {
      this.isSearch = true;
    },
    onSelected(event) {
      this.isSearch = false;
      if (event.preventDefault) {
        event.preventDefault();
      }
    },
    onLanguageChange() {
      this.title = this.translate("create-plan-configuration");
    },
    getFieldState(field) {
      if (field.$error) {
        return "error";
      }
      return "";
    },
    handleIncomingProps(conf, timezones) {
      const allConfigs = this.rosterConfigurationList || [];
      const rosterConfigurations = conf.rosterConfigurations.map((rc) => {
        return allConfigs.find((r) => r.id === rc._id) || { id: rc._id };
      });

      const passedInTimezone = timezones.find((tz) => tz.id === conf.timezone);

      const from = conf.from || new Date();
      const to = conf.to || new Date();

      const scenarioListOptions = getScenariosThatCanBeUsed({
        allScenarios: this.scenarioListOptionsUnfiltered || [],
        rosters: conf.rosterConfigurations,
        startDate: from,
        endDate: to,
      });

      const scenarios =
        scenarioListOptions.length > 0
          ? (conf.scenarios || []).filter((s) =>
              scenarioListOptions.find((sc) => sc._id === s._id)
            )
          : conf.scenarios || [];

      return {
        from,
        to,
        timezone: passedInTimezone || undefined,
        rosterConfigurations,
        forecast: conf.forecast || null,
        scenarios,
        scenarioListOptions: scenarioListOptions || [],
      };
    },
    getOptions(items) {
      return (items && items.map((v) => v.id)) || null;
    },
    getTimezones() {
      const timezones = getTimezonesList().map((tz) => ({
        id: tz,
        name: tz,
      }));
      timezones.sort((a, b) => a.name.localeCompare(b.name));
      return timezones;
    },
    async getForecasts() {
      return Forecast.getForecasts();
    },
    async getScenarios() {
      return Scenario.getScenarios();
    },
    input() {
      this.$emit("input", {
        from: this.from,
        to: this.to,
        timezone: this.timezone?.id,
        rosterConfigurations: this.rosterConfigurations,
        forecast: this.forecast,
        scenarios: this.scenarios,
      });
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.$emit("next", {
        from: this.from,
        to: this.to,
        timezone: this.timezone?.id,
        rosterConfigurations: this.rosterConfigurations,
        forecast: this.forecast,
        scenarios: this.scenarios,
      });
    },
  },
  validations() {
    return this.validationsSchema;
  },
};
</script>

<style lang="scss" scoped></style>
